import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	Input,
	OnInit,
	computed,
	linkedSignal,
	signal,
	viewChild,
	input,
	output
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs'
import { TranslocoDirective } from '@jsverse/transloco'
import { BaseInputComponent } from '@/shared/components/base/base-input/base-input.component'
import { LUCIDE_ICONS, BaseIconComponent } from '@/shared/components/base/base-icon/base-icon.component'
import { CircularLoaderComponent } from '@/shared/components/other/circular-loader.component'

export type SearchResultsLayout = 'dropdown' | 'inline'
export type SearchInputVariant = 'default' | 'panel'

/** Domain-agnostic search field; results are projected in via [results]. */
@Component({
	selector: 'search-input',
	templateUrl: './search-input.component.html',
	styleUrl: './search-input.component.css',
	imports: [
		BaseInputComponent,
		TranslocoDirective,
		CircularLoaderComponent,
		BaseIconComponent
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchInputComponent implements OnInit {
	readonly minSearchChars = input(3)
	readonly debounceMs = input(500)
	readonly allowEnterKey = input(false)
	readonly placeholder = input('actions.search')
	readonly label = input('')
	readonly disabled = input(false)

	@Input() set loading(value: boolean) {
		this.isLoading.set(value)
	}

	@Input() set hasResults(value: boolean) {
		this.resultsAvailable.set(value)
	}

	/** 'dropdown' floats the panel over the page; 'inline' renders it in normal flow. */
	@Input() set resultsLayout(value: SearchResultsLayout) {
		this.layout.set(value)
	}

	/** 'panel' is the roomier modal treatment: wider radius and an accent border. */
	@Input() set variant(value: SearchInputVariant) {
		this.inputVariant.set(value)
	}

	readonly termChanged = output<string>()
	readonly searched = output<string>()
	readonly cleared = output<void>()
	readonly navigated = output<number>()
	readonly submitted = output<void>()

	public readonly SearchIcon = LUCIDE_ICONS['Search']
	public readonly ClearIcon = LUCIDE_ICONS['X']

	protected readonly term = signal('')
	protected readonly hasFocus = signal(false)
	protected readonly isTyping = signal(false)
	protected readonly isLoading = signal(false)

	protected readonly isInline = computed(() => this.layout() === 'inline')

	// An inline panel sits in the layout, so it has no reason to close on blur.
	protected readonly showResults = computed(() =>
		(this.isInline() || this.hasFocus()) &&
		this.term().length > 0 &&
		this.resultsAvailable()
	)

	// Not before a search's first answer, so "no results" never flashes while it
	// loads. Once shown it stays through a reload of the same term (a change of
	// scope), as results do, rather than blinking out with what it holds.
	protected readonly showEmptyState = linkedSignal<{ empty: boolean, loading: boolean }, boolean>({
		source: () => ({
			empty: (this.isInline() || this.hasFocus()) &&
				this.term().length >= this.minSearchChars() &&
				!this.isTyping() &&
				!this.resultsAvailable(),
			loading: this.isLoading()
		}),
		computation: ({ empty, loading }, previous) => empty && (!loading || (previous?.value ?? false))
	})

	protected readonly inputClasses = computed(() =>
		this.inputVariant() === 'panel' ? 'pill pill-lg accent' : 'pill'
	)

	private readonly resultsAvailable = signal(false)
	private readonly layout = signal<SearchResultsLayout>('dropdown')
	private readonly inputVariant = signal<SearchInputVariant>('default')
	private lastSearchedTerm = ''
	private applyNextChangeImmediately = false
	private readonly baseInput = viewChild.required<BaseInputComponent>('baseInput')
	private readonly searchTerm$ = new Subject<string>()

	constructor(private destroyRef: DestroyRef) {}

	ngOnInit(): void {
		this.searchTerm$.pipe(
			debounceTime(this.debounceMs()),
			distinctUntilChanged(),
			takeUntilDestroyed(this.destroyRef)
		).subscribe((term) => {
			this.isTyping.set(false)
			this.emitSearch(term)
		})
	}

	/** Resets the field from the outside, e.g. after a result is picked. */
	public clear(): void {
		this.reset()
		this.hasFocus.set(false)
	}

	/** Moves the caret into the field, e.g. when a hosting modal opens. */
	public focus(): void {
		const input = this.baseInput().inputEl()?.nativeElement
		if (!input) return
		input.focus()
		// Keep the field in the visible area when the mobile keyboard opens.
		input.scrollIntoView({ block: 'nearest', inline: 'nearest' })
	}

	/** Fills the field from the outside, e.g. a recent-search chip, and searches at once. */
	public setTerm(term: string): void {
		this.applyNextChangeImmediately = true
		this.hasFocus.set(true)
		this.baseInput().writeValue(term)
	}

	protected handleClearClick(event: Event): void {
		// mousedown would blur the field first; keep the caret where the user left it.
		event.preventDefault()
		this.reset()
	}

	protected handleValueChange(value: string | number): void {
		const term = String(value)
		this.term.set(term)
		this.termChanged.emit(term)
		this.searchTerm$.next(term)

		if (this.applyNextChangeImmediately) {
			this.applyNextChangeImmediately = false
			this.isTyping.set(false)
			this.emitSearch(term)
			return
		}

		this.isTyping.set(term.length > 0)
	}

	protected handleEnter(): void {
		if (this.allowEnterKey()) {
			this.submitted.emit()
		}
	}

	protected handleArrowKey(event: Event, direction: number): void {
		event.preventDefault()
		this.navigated.emit(direction)
	}

	protected onFocus(): void {
		this.hasFocus.set(true)
		this.baseInput().inputEl()?.nativeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' })
	}

	protected onBlur(): void {
		this.hasFocus.set(false)
	}

	private reset(): void {
		this.baseInput().writeValue('')
		this.isTyping.set(false)
		this.lastSearchedTerm = ''
		this.cleared.emit()
	}

	/** Guards against the debounced stream repeating a search setTerm already ran. */
	private emitSearch(term: string): void {
		if (term.length < this.minSearchChars()) {
			this.lastSearchedTerm = ''
			this.cleared.emit()
			return
		}
		if (term === this.lastSearchedTerm) {
			return
		}
		this.lastSearchedTerm = term
		this.searched.emit(term)
	}
}
